/**
 * Highlight (≠ filter) state for the migration-task pages. The two
 * surfaces (TaskListPage / DagPage) share a small set of multi-select
 * controls — 領域 + 担当 — that *dim* non-matching rows / cards instead
 * of hiding them. Keeping the matching tasks in place preserves DAG
 * structure (edges still make sense) and list density (counts don't
 * change), which is the explicit user requirement spelled out in the
 * spec: "該当しないものを隠さない、目立たなくする".
 *
 * The state is encoded into URL query params so a reload preserves the
 * current emphasis, and so users can deep-link a colleague to "show me
 * tasks in 会計領域 owned by 鈴木".
 *
 * Param shape:
 *   ?highlightArea=A,B&highlightAssignee=X,Y
 *
 * Both lists default to empty (= no highlighting). The matching rule is
 * AND across categories: a task is highlighted only when it matches all
 * non-empty categories. Within a single category the rule is OR
 * (matching ANY of the selected values is enough).
 */

import { readTaskAreaIds } from '@mct/shared';

export interface HighlightState {
  /**
   * Selected area labels. Empty array means "no area filter — every
   * area passes the area predicate". A task whose `areaId` is null is
   * never highlighted by an active area selection.
   */
  areaIds: readonly string[];
  /**
   * Selected assignee IDs. Empty array means "no assignee filter".
   * Includes the {@link UNASSIGNED_KEY} sentinel when the user wants
   * to emphasise tasks without an assignee.
   */
  assigneeIds: readonly string[];
}

/** Sentinel value for "未割当" in the assignee picker / URL serialiser. */
export const UNASSIGNED_KEY = '__none__';

export const EMPTY_HIGHLIGHT: HighlightState = {
  areaIds: [],
  assigneeIds: [],
};

/** True when no category has any selection (= the controls are a no-op). */
export function isEmptyHighlight(state: HighlightState): boolean {
  return state.areaIds.length === 0 && state.assigneeIds.length === 0;
}

/**
 * Decide whether a task should render at "full attention" given the
 * current highlight state. When the state is empty, *every* task is
 * highlighted (no-op). Otherwise the AND-of-categories rule applies.
 *
 * The function is intentionally generic over the row shape: callers can
 * project a task / row / DAG node down to `{areaId, assigneeId}` without
 * leaking page-level types into the lib layer.
 */
export function isTaskHighlighted(
  task: {
    areaIds?: readonly string[] | null;
    areaId?: string | null;
    assigneeId: string | null | undefined;
  },
  state: HighlightState,
): boolean {
  if (isEmptyHighlight(state)) return true;
  if (state.areaIds.length > 0) {
    // 領域 is multi-value: highlight if the task carries ANY selected area.
    const ids = readTaskAreaIds(task);
    if (!ids.some((id) => state.areaIds.includes(id))) return false;
  }
  if (state.assigneeIds.length > 0) {
    const key = task.assigneeId ?? UNASSIGNED_KEY;
    if (!state.assigneeIds.includes(key)) return false;
  }
  return true;
}

/* -------------------------------------------------------------------- */
/*  URL query (de)serialisation                                          */
/* -------------------------------------------------------------------- */

const PARAM_AREA = 'highlightArea';
const PARAM_ASSIGNEE = 'highlightAssignee';

/**
 * Decode the highlight state from a `URLSearchParams`. Unknown keys are
 * left untouched in the input. Empty / missing values produce
 * {@link EMPTY_HIGHLIGHT}.
 */
export function decodeHighlightFromParams(
  params: URLSearchParams,
): HighlightState {
  return {
    areaIds: parseList(params.get(PARAM_AREA)),
    assigneeIds: parseList(params.get(PARAM_ASSIGNEE)),
  };
}

/**
 * Mutate-free encode that returns a fresh `URLSearchParams`. Existing
 * unrelated keys on `base` are preserved so the highlight controls
 * compose cleanly with the page's other query params (run / sort).
 */
export function encodeHighlightToParams(
  state: HighlightState,
  base?: URLSearchParams,
): URLSearchParams {
  const out = new URLSearchParams(base);
  out.delete(PARAM_AREA);
  out.delete(PARAM_ASSIGNEE);
  if (state.areaIds.length > 0) {
    out.set(PARAM_AREA, state.areaIds.join(','));
  }
  if (state.assigneeIds.length > 0) {
    out.set(PARAM_ASSIGNEE, state.assigneeIds.join(','));
  }
  return out;
}

function parseList(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Toggle a single value in a list. Pure helper used by the multi-select
 * UI; lifted here so the toggle semantics ship to anyone reusing
 * {@link HighlightState} downstream.
 */
export function toggleHighlightValue(
  list: readonly string[],
  value: string,
): string[] {
  return list.includes(value)
    ? list.filter((v) => v !== value)
    : [...list, value];
}
